"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"

const DailyDevotions = () => {
  const [devotions, setDevotions] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    youtube_url: "",
    date: new Date().toISOString().split("T")[0],
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    loadDevotions()
  }, [])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadDevotions = async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await apiService.getDevotions()
      setDevotions(response)
    } catch (error) {
      console.error('Error loading devotions:', error)
      setError(error.message || 'Failed to load devotions')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const standardizeYouTubeUrl = (url) => {
    try {
      if (!url) {
        console.error('Empty URL provided');
        return '';
      }
      
      // Remove any whitespace
      url = url.trim();
      console.log('Original URL:', url);
      
      // Handle different YouTube URL formats
      let videoId = '';
      
      try {
        // Handle youtu.be links
        if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1];
          console.log('Extracted videoId from youtu.be:', videoId);
        }
        // Handle youtube.com/watch?v= links
        else if (url.includes('youtube.com/watch')) {
          const urlObj = new URL(url);
          videoId = urlObj.searchParams.get('v');
          console.log('Extracted videoId from youtube.com/watch:', videoId);
        }
        // Handle youtube.com/embed/ links
        else if (url.includes('youtube.com/embed/')) {
          videoId = url.split('youtube.com/embed/')[1];
          console.log('Extracted videoId from youtube.com/embed:', videoId);
        }
        // Handle youtube.com/v/ links
        else if (url.includes('youtube.com/v/')) {
          videoId = url.split('youtube.com/v/')[1];
          console.log('Extracted videoId from youtube.com/v:', videoId);
        }
        // Handle youtube.com/live/ links
        else if (url.includes('youtube.com/live/')) {
          videoId = url.split('youtube.com/live/')[1];
          console.log('Extracted videoId from youtube.com/live:', videoId);
        }
        
        // Remove any additional parameters after the video ID
        if (videoId) {
          videoId = videoId.split('&')[0];
          videoId = videoId.split('?')[0]; // Remove query parameters
          const standardizedUrl = `https://www.youtube.com/watch?v=${videoId}`;
          console.log('Standardized URL:', standardizedUrl);
          return standardizedUrl;
        }
        
        console.warn('Could not extract videoId from URL:', url);
        return url;
      } catch (error) {
        console.error('Error processing URL:', error);
        return url;
      }
    } catch (error) {
      console.error('Error in standardizeYouTubeUrl:', error);
      return url;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setIsLoading(true)
      setError("")

      const devotionData = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
      }

      // Validate and standardize YouTube URL if provided
      if (formData.youtube_url) {
        const youtubeUrl = formData.youtube_url.trim()
        console.log('Original YouTube URL:', youtubeUrl);
        
        if (!youtubeUrl.match(/^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/)) {
          throw new Error('Please enter a valid YouTube URL')
        }
        
        // Standardize the YouTube URL
        const standardizedUrl = standardizeYouTubeUrl(youtubeUrl)
        console.log('Final standardized URL:', standardizedUrl)
        
        if (!standardizedUrl.includes('youtube.com/watch?v=')) {
          throw new Error('Invalid YouTube URL format after standardization')
        }
        
        devotionData.youtube_url = standardizedUrl
      }

      await apiService.createDevotion(devotionData)
      setSuccessMessage("Devotion created successfully!")
      setFormData({
        title: "",
        description: "",
        youtube_url: "",
        date: new Date().toISOString().split("T")[0],
      })
      setShowForm(false)
      loadDevotions()
    } catch (error) {
      console.error('Error creating devotion:', error)
      setError(error.message || 'Failed to create devotion')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this devotion?")) {
      try {
        setIsLoading(true)
        setError("")
        await apiService.deleteDevotion(id)
        setSuccessMessage("Devotion deleted successfully!")
        loadDevotions()
      } catch (error) {
        console.error('Error deleting devotion:', error)
        setError(error.message || 'Failed to delete devotion')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const getYouTubeId = (url) => {
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|.+\?v=)?)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  }

  return (
    <div className="daily-devotions">
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="action-buttons">
        <button className="btn" onClick={() => setShowForm(!showForm)} disabled={isLoading}>
          {showForm ? "Cancel" : "Add New Devotion"}
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2 className="form-title">Add New Daily Devotion</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                className="form-control"
                value={formData.title}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                className="form-control"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                required
                disabled={isLoading}
              ></textarea>
            </div>

            <div className="form-group">
              <label htmlFor="youtube_url">YouTube URL</label>
              <input
                type="url"
                id="youtube_url"
                name="youtube_url"
                className="form-control"
                value={formData.youtube_url}
                onChange={handleInputChange}
                placeholder="https://www.youtube.com/watch?v=..."
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                name="date"
                className="form-control"
                value={formData.date}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Devotion"}
              </button>
            </div>
          </form>
        </div>
      )}

      <h2 className="section-title">All Daily Devotions</h2>
      
      {isLoading && !showForm ? (
        <div className="loading">Loading devotions...</div>
      ) : devotions.length > 0 ? (
        <div className="devotions-list">
          {devotions.map((devotion) => (
            <div className="devotion-card card" key={devotion.id}>
              <div className="devotion-video">
                {devotion.youtube_url && (
                  <div className="video-placeholder">
                    <img src={`https://img.youtube.com/vi/${getYouTubeId(devotion.youtube_url)}/maxresdefault.jpg`} alt="Video thumbnail" style={{ width: '100%', maxWidth: '320px', height: 'auto' }} />
                    <div className="play-icon">
                      <i className="material-icons">play_circle_filled</i>
                    </div>
                  </div>
                )}
              </div>
              
              <h3>{devotion.title}</h3>
              <p className="devotion-date">{new Date(devotion.date).toLocaleDateString()}</p>
              <p className="devotion-description">{devotion.description}</p>
              <div className="devotion-actions">
                <button className="action-btn" onClick={() => handleDelete(devotion.id)} disabled={isLoading}>
                  <i className="material-icons">delete</i>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <p>No devotions found. Add your first devotion!</p>
        </div>
      )}
    </div>
  )
}

export default DailyDevotions
